/**
 * Profile — F 345 THE PROFILE FACE.
 *
 * Three sections, in order, behind a single Modal that opens from the top-bar
 * avatar menu:
 *   1. Identity  — who the current user is (Principal scope + id).
 *   2. AI Engine — the F 331 engine state rendered as a small face:
 *                  capability pie + provider slots. The state comes from the
 *                  same `useAiEngine` hook the Settings → AI Engine tab uses,
 *                  so the two views never disagree.
 *   3. Settings  — the F 324 appearance controls (ModeTheme, Font Size). They
 *                  write on change. There is no Save button by design; F 324
 *                  made the page that cannot lose what the user typed.
 *
 * LOG OUT IN DEV MODE: the dispatch says "in dev mode, this just resets to
 * `operator-1`". The principal in dev IS `operator-1`, so logging out is a no-op
 * for the principal itself — what it does is clear the saved appearance and
 * theme from localStorage so the next user gets a clean default. The button is
 * honest about what dev mode can do.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Group,
  Stack,
  Title,
  Text,
  Badge,
  Divider,
  Modal,
  ScrollArea,
  Paper,
  Select,
  Slider,
  Button,
} from '@mantine/core';
import {
  IconUser, IconSparkles, IconSettings, IconLogout, IconSun, IconMoon,
  IconMail, IconShield, IconKey, IconCheck, IconX,
} from '@tabler/icons-react';
import { operatorPrincipal, type Principal } from '../../../core/credentials/credentialStore';
import { SettingStore } from '../../../core/settings/SettingStore';
import {
  loadSettingStore,
  saveSettingStore,
  readLegacyPreferences,
  legacyKeyFor,
  type LegacyPreferences,
} from '../../../core/settings/settingHost';
import { writeAppSetting } from '../../../core/settings/appSettings';
import { SlotList } from '../settings/PipelineFaces';
import { TelemetryFace } from '../telemetry/TelemetryFace';
import { createGraphTelemetrySink } from '../../../core/telemetry/telemetrySink';
import { useAiEngine } from '../settings/useAiEngine';
import { useMantineColorScheme } from '@mantine/core';
import { buildSlices, sliceAngles, type CapabilitySlice } from '../../../core/registry/engineState';
import { useTheme as useFcTheme } from '../../themes';

// F 345 — dev-mode principal. The dispatch says "in dev mode, this just resets
// to `operator-1`", and the operator principal `operator-1` is the same one
// the credential store seeds when no auth adapter is registered.
const DEV_PRINCIPAL: Principal = operatorPrincipal('operator-1');

export interface ProfileProps {
  opened: boolean;
  onClose: () => void;
  /** The current principal. In dev mode, defaults to operator-1. */
  principal?: Principal;
  /** Override for tests; production hydrates from storage. */
  storeOverride?: SettingStore | null;
}

// ── 1. Identity section ────────────────────────────────────────────────────
const IdentitySection: React.FC<{ principal: Principal }> = ({ principal }) => {
  return (
    <Paper withBorder p="md" radius="md" data-testid="profile-identity">
      <Group gap="xs" mb="xs">
        <IconUser size={16} color="var(--fc-accent-primary)" />
        <Title order={4} size="h5">Identity</Title>
        <Badge size="xs" variant="light" color="blue" data-testid="profile-principal-scope">
          scope: {principal.scope}
        </Badge>
      </Group>
      <Stack gap={4}>
        <Group gap="xs">
          <IconShield size={14} color="var(--fc-text-hint)" />
          <Text size="sm" fw={500} data-testid="profile-principal-id">
            {principal.id}@{principal.scope}
          </Text>
        </Group>
        <Group gap="xs">
          <IconMail size={14} color="var(--fc-text-hint)" />
          <Text size="sm" c="dimmed" data-testid="profile-principal-email">
            {principal.id}@freshcards.local
          </Text>
        </Group>
        <Group gap="xs">
          <IconKey size={14} color="var(--fc-text-hint)" />
          <Text size="xs" c="dimmed" data-testid="profile-principal-note">
            Dev mode — credentials are kept in this browser only.
          </Text>
        </Group>
      </Stack>
    </Paper>
  );
};

// ── 2. AI Engine section ───────────────────────────────────────────────────
//
// A small, self-contained engine face: a pie of capability coverage + a
// provider slot list. The data comes from the same `useAiEngine` hook the
// Settings page uses, so the two views are guaranteed to read the same graph
// state and never drift.

const CapabilityPie: React.FC<{ slices: CapabilitySlice[] }> = ({ slices }) => {
  const angles = useMemo(() => sliceAngles(slices), [slices]);
  const hasAny = slices.length > 0;
  if (!hasAny) {
    return (
      <Paper withBorder p="sm" radius="sm" data-testid="profile-ai-pie-empty">
        <Text size="xs" c="dimmed">No provider connections yet — the engine is idle.</Text>
      </Paper>
    );
  }
  // SVG pie
  const size = 96; const cx = size / 2; const cy = size / 2; const r = 36;
  const sliceColor: Record<CapabilitySlice['health'], string> = {
    strong: '#2f9e44', limited: '#f0b429', missing: '#868e96',
  };
  function point(deg: number): [number, number] {
    const rad = ((deg - 90) * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  }
  function arcPath(start: number, deg: number): string {
    if (deg >= 359.999) return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx - 0.01} ${cy - r} Z`;
    const [x1, y1] = point(start);
    const [x2, y2] = point(start + deg);
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${deg > 180 ? 1 : 0} 1 ${x2} ${y2} Z`;
  }
  return (
    <Group gap="sm" align="center" data-testid="profile-ai-pie">
      <svg width={size} height={size} role="img" aria-label="Provider capability pie">
        {angles.map((a) => (
          <path key={a.capability} d={arcPath(a.start, a.degrees)} fill={sliceColor[slices.find((s) => s.capability === a.capability)!.health]} stroke="var(--fc-bg-primary)" strokeWidth={1} />
        ))}
        <circle cx={cx} cy={cy} r={r * 0.5} fill="var(--fc-bg-primary)" />
      </svg>
      <Stack gap={2}>
        {slices.map((s) => (
          <Group key={s.capability} gap={6}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: sliceColor[s.health] }} />
            <Text size="xs">{s.capability}</Text>
            <Text size="xs" c="dimmed">— {s.health}</Text>
          </Group>
        ))}
      </Stack>
    </Group>
  );
};

const AiEngineSection: React.FC<{
  store: SettingStore | null;
  telemetrySink: ReturnType<typeof createGraphTelemetrySink>;
}> = ({ store, telemetrySink }) => {
  // F 345 — `now` must be stable across renders, otherwise `useAiEngine`
  // (which memoizes on it) recomputes every render and produces a new
  // `engine.state.slots` reference each time. The downstream `useMemo`
  // for slices then re-fires forever. Pin `now` to mount time.
  const now = useMemo(() => Date.now(), []);
  const engine = useAiEngine({ store, now, window: 'today' });
  const slices = useMemo(
    () => buildSlices([], new Map(), now, new Map()),
    // engine.state.slots is the only thing that can change; rebuild when it does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [engine.state.slots, now],
  );
  return (
    <Paper withBorder p="md" radius="md" data-testid="profile-ai-engine">
      <Group gap="xs" mb="xs">
        <IconSparkles size={16} color="var(--fc-accent-primary)" />
        <Title order={4} size="h5">AI Engine</Title>
        <Badge size="xs" variant="light" color="grape" data-testid="profile-ai-connected">
          {engine.state.slots.filter((s) => s.status === 'connected').length} / {engine.state.slots.length} slots connected
        </Badge>
      </Group>
      <Stack gap="sm">
        <Box>
          <Text size="xs" c="dimmed" mb={4}>Capability coverage</Text>
          <CapabilityPie slices={slices} />
        </Box>
        <Box>
          <Text size="xs" c="dimmed" mb={4}>Provider slots</Text>
          <SlotList slots={engine.state.slots} />
        </Box>
        <Box>
          <TelemetryFace sink={telemetrySink} title="Live signals" emptyHint="no live signals — open Settings → AI Engine to investigate a provider." />
        </Box>
      </Stack>
    </Paper>
  );
};

// ── 3. Settings section (F 324 appearance controls) ────────────────────────
const SettingsSection: React.FC<{
  store: SettingStore | null;
  preferences: LegacyPreferences | null;
  onPreferencesChange: (next: LegacyPreferences) => void;
}> = ({ store, preferences, onPreferencesChange }) => {
  const { setColorScheme } = useMantineColorScheme();
  // F 342 — the Settings ModeTheme select only told Mantine + `data-theme`.
  // The page wrapper (ThemeProvider) was using its own `modeName` state,
  // so `--fc-text-primary` / `--fc-bg-primary` did not flip and the body
  // colour stayed light while the Mantine Paper flipped dark. The hook
  // ties the two together.
  const { setMode: setFcTheme } = useFcTheme();
  if (!preferences) {
    return (
      <Paper withBorder p="md" radius="md" data-testid="profile-settings">
        <Text size="sm" c="dimmed">Loading settings…</Text>
      </Paper>
    );
  }

  // F 324 — write-on-change. No Save button. The page that can lose what the
  // user typed is the page that needs a Save button, and this one does not.
  const writePreference = (key: string, value: unknown) => {
    if (!store) return;
    const res = writeAppSetting(store, key, value);
    if (!res.ok) return;
    void saveSettingStore(store);
    onPreferencesChange({ ...preferences, [key.split('.').pop()!]: value } as LegacyPreferences);
  };

  const applyTheme = (next: 'light' | 'dark' | 'system') => {
    const finalTheme: 'light' | 'dark' | 'auto' = next === 'system'
      ? (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : next;
    setColorScheme(finalTheme);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', finalTheme);
    }
    // F 342 — adopt the new theme in the ThemeProvider too, so the
    // --fc-text-primary / --fc-bg-primary tokens flip and the body
    // background + contrast come from the same source.
    const fcMode: 'light' | 'dark' = next === 'system'
      ? (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : next;
    setFcTheme(fcMode);
  };

  const handleThemeChange = (v: string | null) => {
    const next = (v ?? 'system') as 'light' | 'dark' | 'system';
    writePreference(legacyKeyFor('theme'), next);
    applyTheme(next);
  };

  // F 342 — apply fontSize to the document the moment the slider moves.
  // The slider's onChange writes to the graph AND to the DOM, so a
  // preview is immediate and the persistence is the side effect.
  const handleFontSizeChange = (v: number) => {
    writePreference(legacyKeyFor('fontSize'), v);
    if (typeof document !== 'undefined') {
      document.documentElement.style.fontSize = `${v}px`;
    }
  };

  return (
    <Paper withBorder p="md" radius="md" data-testid="profile-settings">
      <Group gap="xs" mb="xs">
        <IconSettings size={16} color="var(--fc-accent-primary)" />
        <Title order={4} size="h5">Settings</Title>
        <Badge size="xs" variant="light" color="green" leftSection={<IconCheck size={10} />}>
          writes immediately
        </Badge>
      </Group>
      <Stack gap="md">
        <Group justify="space-between" align="center" data-testid="profile-settings-theme">
          <Group gap="xs">
            {preferences.theme === 'dark' ? <IconMoon size={16} /> : <IconSun size={16} />}
            <Text size="sm">ModeTheme</Text>
          </Group>
          <Select
            value={preferences.theme}
            onChange={handleThemeChange}
            data={[
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
              { value: 'system', label: 'System' },
            ]}
            size="xs"
            w={140}
            allowDeselect={false}
            data-testid="profile-theme-select"
          />
        </Group>
        <Group justify="space-between" align="center" data-testid="profile-settings-font">
          <Text size="sm">Font Size</Text>
          <Group gap="xs" align="center">
            <Text size="xs" c="dimmed">{preferences.fontSize}px</Text>
            <Slider
              value={preferences.fontSize}
              onChange={handleFontSizeChange}
              min={12}
              max={24}
              step={1}
              w={160}
              size="xs"
              label={(v) => `${v}px`}
              data-testid="profile-font-size"
            />
          </Group>
        </Group>
        <Group gap={4}>
          <IconX size={12} color="var(--fc-text-hint)" />
          <Text size="xs" c="dimmed">No Save button — changes are persisted the moment you make them.</Text>
        </Group>
      </Stack>
    </Paper>
  );
};

// ── Composed Profile Face ──────────────────────────────────────────────────
const ProfileInner: React.FC<{
  principal: Principal;
  onClose: () => void;
  storeOverride?: SettingStore | null;
}> = ({ principal, onClose, storeOverride }) => {
  // The Setting graph is the source of truth (F 324). We hydrate from storage
  // on mount; after every write we update the local view so what we render is
  // what the graph holds.
  const [store, setStore] = useState<SettingStore | null>(storeOverride ?? null);
  const [preferences, setPreferences] = useState<LegacyPreferences | null>(null);

  useEffect(() => {
    const DEFAULTS: LegacyPreferences = { theme: 'system', fontSize: 14, autoSave: true, aiAssistant: false, defaultView: 'grid', compactMode: false, showSidebar: true, sidebarPosition: 'left', primaryColor: 'blue', accentColor: 'violet' };
    if (storeOverride !== undefined) {
      setStore(storeOverride);
      // A test override is a synchronous convenience: derive the preferences
      // view from the store immediately, so the Settings face renders in the
      // same render where the store is set.
      setPreferences(readLegacyPreferences() ?? DEFAULTS);
      return;
    }
    let cancelled = false;
    void loadSettingStore()
      .then((result) => {
        if (cancelled) return;
        setStore(result.store);
        setPreferences(readLegacyPreferences() ?? DEFAULTS);
      })
      .catch(() => {
        if (cancelled) return;
        setStore(null);
        setPreferences(DEFAULTS);
      });
    return () => { cancelled = true; };
  }, [storeOverride]);

  const telemetrySink = useMemo(() => createGraphTelemetrySink(store ?? new SettingStore({ initial: [] })), [store]);

  const handleLogOut = () => {
    // F 345 — dev mode: "reset to operator-1". The principal IS operator-1, so
    // the identity half is already correct. What we reset is the local
    // appearance (theme + theme in localStorage) so the next user gets a
    // clean default. The button is honest about what dev mode can do.
    try {
      localStorage.removeItem('freshcards-theme');
      localStorage.removeItem('freshcards-skin');
    } catch {
      // localStorage can be unavailable in private mode; we still close.
    }
    onClose();
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconUser size={18} color="var(--fc-accent-primary)" />
          <Text fw={600} data-testid="profile-title">Profile</Text>
        </Group>
      }
      size="lg"
      centered
      scrollAreaComponent={ScrollArea.Autosize}
      data-testid="profile-face"
    >
      <Stack gap="md">
        <IdentitySection principal={principal} />
        <Divider label="Engine" labelPosition="left" />
        <AiEngineSection store={store} telemetrySink={telemetrySink} />
        <Divider label="Settings" labelPosition="left" />
        <SettingsSection
          store={store}
          preferences={preferences}
          onPreferencesChange={setPreferences}
        />
        <Divider />
        <Group justify="space-between" align="center">
          <Text size="xs" c="dimmed" data-testid="profile-principal-confirm">
            Signed in as <b>{principal.id}</b> ({principal.scope})
          </Text>
          <Button
            color="red"
            variant="light"
            leftSection={<IconLogout size={14} />}
            onClick={handleLogOut}
            data-testid="profile-logout"
          >
            Log out
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

/**
 * F 345 — Public Profile Face.
 *
 * NOT wrapped in a ThemeProvider — the host app already provides one, and a
 * second provider would be a SEPARATE context, so a theme change here would
 * not propagate to the rest of the app. The Mantine theme hook still works
 * because MantineProvider is the only one we need at this level and the host
 * already supplies it.
 *
 * Mount this anywhere in the tree (we mount it in WorkspaceHeader) and pass
 * `opened` + `onClose` from the parent.
 */
export const Profile: React.FC<ProfileProps> = ({ opened, onClose, principal, storeOverride }) => {
  if (!opened) return null;
  const p = principal ?? DEV_PRINCIPAL;
  return <ProfileInner principal={p} onClose={onClose} storeOverride={storeOverride} />;
};

export default Profile;
